/**
 * Rudderstack loader will fetch the plugins during runtime as federated modules in separate requests.
 *
 * Documentation as reference:
 *  - https://www.npmjs.com/package/@rudderstack/analytics-js?activeTab=readme
 *  - https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/
 *  - https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/migration-guide/#migration-considerations
 *  - https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/#queueopts
 *  - https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/#plugins
 *
 */
import { RudderAnalytics } from '@rudderstack/analytics-js';

import OneTrustController from 'controllers/OneTrustController';
import DataDogLogs from 'utils/DataDogLogs';
import Settings from 'utils/Settings';

import { RS_DATA_PLANE_URL, RS_CONSENT_PROVIDER_ONE_TRUST } from './constants';

const _isRudderAnalyticsLoaded = () => {
  return window.rudderanalytics && !Array.isArray(window.rudderanalytics);
};

const _getRudderAnalyticsInstance = () => {
  if (_isRudderAnalyticsLoaded()) {
    return window.rudderanalytics;
  }

  return new RudderAnalytics();
};

const _rudderAnalyticsLoadWithOptions = () => {
  try {
    const analyticsInstance = _getRudderAnalyticsInstance();

    analyticsInstance.load(Settings.rudderstackAppId, RS_DATA_PLANE_URL, {
      // Load native integration with Amplitude and Google Analytics
      loadIntegration: false,

      // For retrocompatibility use the legacy encryption
      storage: {
        encryption: {
          version: 'legacy',
        },
      },

      // Enable consent management with OneTrust
      consentManagement: {
        enabled: true,
        provider: RS_CONSENT_PROVIDER_ONE_TRUST,
      },

      // Queue system
      queueOpts: {
        maxRetryDelay: 360000,
        minRetryDelay: 1000,
        backoffFactor: 2,
        backoffJitter: 0,
        maxAttempts: 10,
        maxItems: 100,
        batch: {
          enabled: false, // TODO this is disabled by default, evaulate if we want to enable it
          maxItems: 100,
          maxSize: 512 * 1024, // 512 KB
          flushInterval: 60000, // In ms
        },
      },

      // Hook to load event
      onLoaded: () => {
        DataDogLogs.warn('Rudderstack sdk - initialized and loaded succesfully');
      },
    });

    window.rudderanalytics = analyticsInstance;
  } catch (error) {
    DataDogLogs.error('Rudderstack sdk error - on load with options error', {}, error);
  }
};

const _initRudderAnalyticsWithRetry = () => {
  const SDK_LOADED_ATTEMPTS = 60;
  const SDK_LOADED_ATTEMPTS_INTERVAL_MS = 1000;
  let loadsAttempts = 1;

  const intervalID = setInterval(function () {
    if (!_isRudderAnalyticsLoaded()) {
      _rudderAnalyticsLoadWithOptions();

      clearInterval(intervalID);
    } else {
      if (loadsAttempts > SDK_LOADED_ATTEMPTS) {
        clearInterval(intervalID);

        DataDogLogs.error(
          `Rudderstack sdk error - could not be loaded - after ${loadsAttempts} attempts`
        );
      }

      loadsAttempts++;
    }
  }, SDK_LOADED_ATTEMPTS_INTERVAL_MS);
};

const loadRudderstackSdk = () => {
  try {
    OneTrustController.waitForOneTrust()
      .then(() => {
        _initRudderAnalyticsWithRetry();
      })
      .catch(error => {
        DataDogLogs.error('Rudderstack sdk error - OneTrust waitForOneTrust error', {}, error);
      });
  } catch (error) {
    DataDogLogs.error('Rudderstack sdk error - OneTrust Controller global error', {}, error);
  }
};

export { loadRudderstackSdk };
